import React, { useEffect, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import './DashboardCalendar.css';
import Modal from './Modal';
import CourseEditModal2 from './CourseEditModal2';
import { apiFetch } from "../api";

export default function DashboardCalendar({ semesterShort }) {
  const [allEvents, setAllEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);

  // Auswahl-States
  const [selectedDegree, setSelectedDegree] = useState('');
  const [selectedSemester, setSelectedSemester] = useState('');
  // Bachelor
  const [selectedAppBSc, setSelectedAppBSc] = useState('');
  // Master
  const [mscApp1, setMscApp1] = useState('');
  const [mscApp2, setMscApp2] = useState('');
  const [mscDis1, setMscDis1] = useState('');
  const [mscDis2, setMscDis2] = useState('');

  // Modals
  const [modalEvent, setModalEvent] = useState(null);
  const [editId, setEditId] = useState(null);

  // Feste Listen (Master)
  const masterAppsList = [
    'Anwendungsmodul Autonome Systeme',
    'Anwendungsmodul Circular Economy und Umwelttechnik',
    'Anwendungsmodul Digitale Transformation',
    'Anwendungsmodul Energie',
    'Anwendungsmodul Industrie 4.0',
    'Anwendungsmodul Mobilität'
  ];

  const masterDiszList = [
    'Cooperative Human-Machine Interaction',
    'Engineering Methods and Dependability',
    'Machine Learning and Big Data',
    'Smart Cyber-Physical Systems',
    'Auflage'
  ];

  const alwaysApps = [
    'Zentrale Veranstaltung',
    'Zentrale Veranstaltungen',
    'Projekt',
    'Projekte und Abschlussarbeit',
    'Abschlussarbeit',
  ];

  const baseAppsBSc = ['Basismodul Mathematik', 'Basismodul Informatik'];

  const loadEvents = () => {
    if (!semesterShort) return;
    apiFetch(`get_calendar_events.php?semester=${encodeURIComponent(semesterShort)}`)
      .then(res => res.json())
      .then(data => {
        const arr = Array.isArray(data) ? data : [];
        setAllEvents(arr);
        setFilteredEvents(arr);
      })
      .catch(console.error);
  };

  useEffect(() => {
    loadEvents();
  }, [semesterShort]);

  const bachelorAppOptions = Array.from(
    new Set(
      allEvents
        .filter(e => e.course_degree === 'Bachelor')
        .map(e => e.course_application)
        .filter(Boolean)
    )
  );

  useEffect(() => {
    let out = allEvents;

    if (selectedDegree) {
      out = out.filter(e => e.course_degree === selectedDegree);
    }

    if (selectedSemester) {
      const semNum = parseInt(selectedSemester, 10);
      out = out.filter(e => Number(e.course_semester) === semNum);
    }

    if (selectedDegree === 'Bachelor' && selectedAppBSc) {
      const allowed = new Set([selectedAppBSc, ...alwaysApps, ...baseAppsBSc]);
      out = out.filter(e => allowed.has(e.course_application));
    }

    if (selectedDegree === 'Master') {
      const chosenApps = [mscApp1, mscApp2].filter(Boolean);
      const chosenDis = [mscDis1, mscDis2].filter(Boolean);

      if (chosenApps.length || chosenDis.length) {
        const allowed = new Set([...alwaysApps, ...chosenApps, ...chosenDis]);
        out = out.filter(e => allowed.has(e.course_application));
      }
    }

    setFilteredEvents(out);
  }, [
    allEvents,
    selectedDegree,
    selectedSemester,
    selectedAppBSc,
    mscApp1,
    mscApp2,
    mscDis1,
    mscDis2
  ]);

  const onDegreeChange = (v) => {
    setSelectedDegree(v);
    setSelectedSemester('');
    setSelectedAppBSc('');
    setMscApp1('');
    setMscApp2('');
    setMscDis1('');
    setMscDis2('');
  };

  const CATEGORY_COLORS = {
    informatik: 'rgb(156,198,215)',
    digitalisierung: '#B39399',
    anwendungsgebiet: '#F2C8A5',
  };

  function getCategory(app) {
    const infMathe = ['Basismodul Mathematik', 'Basismodul Informatik'];
    const digiProj = [
      'Zentrale Veranstaltung',
      'Zentrale Veranstaltungen',
      'Projekt',
      'Projekte und Abschlussarbeit',
      'Abschlussarbeit'
    ];

    if (infMathe.includes(app)) return 'informatik';
    if (digiProj.includes(app)) return 'digitalisierung';
    return 'anwendungsgebiet';
  }

  const calendarEvents = filteredEvents.map(e => {
    const color = CATEGORY_COLORS[getCategory(e.course_application)];
    const manuallyEdited = Number(e.manually_edited) === 1;

    return {
      id: e.id,
      title: e.title,
      start: e.start,
      end: e.end,
      backgroundColor: color,
      borderColor: color,
      textColor: '#000',
      classNames: manuallyEdited ? ['event-edited'] : [],
      extendedProps: {
        id: e.id,
        course_degree: e.course_degree,
        course_semester: e.course_semester,
        course_application: e.course_application,
        origin: e.course_origin,
        location: e.course_location,
        lecturer: e.course_lecturer,
        manuallyEdited
      }
    };
  });

  const isDisabled = (value, other) => value && value === other;

  return (
    <div className="card">
      <h3>Kalender: Termine im Semester {semesterShort}</h3>

      <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <label htmlFor="degree">Studiengang:</label><br />
          <select id="degree" value={selectedDegree} onChange={(e) => onDegreeChange(e.target.value)}>
            <option value="">Alle</option>
            <option value="Bachelor">Bachelor</option>
            <option value="Master">Master</option>
          </select>
        </div>

        <div>
          <label htmlFor="semester">Semester:</label><br />
          <select id="semester" value={selectedSemester} onChange={(e) => setSelectedSemester(e.target.value)}>
            <option value="">Alle</option>
            {[1, 2, 3, 4, 5, 6].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>

        {selectedDegree === 'Bachelor' && (
          <div>
            <label htmlFor="appB">Anwendungsgebiet:</label><br />
            <select
              id="appB"
              value={selectedAppBSc}
              onChange={(e) => setSelectedAppBSc(e.target.value)}
            >
              <option value="">Alle</option>
              {bachelorAppOptions.map((app, i) => (
                <option key={i} value={app}>{app}</option>
              ))}
            </select>
          </div>
        )}

        {selectedDegree === 'Master' && (
          <>
            <div>
              <label htmlFor="app1">Anwendungsgebiet 1:</label><br />
              <select id="app1" value={mscApp1} onChange={(e) => setMscApp1(e.target.value)}>
                <option value="">Alle</option>
                {masterAppsList.map((app, i) => (
                  <option key={i} value={app} disabled={isDisabled(app, mscApp2)}>{app}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="app2">Anwendungsgebiet 2:</label><br />
              <select id="app2" value={mscApp2} onChange={(e) => setMscApp2(e.target.value)}>
                <option value="">Alle</option>
                {masterAppsList.map((app, i) => (
                  <option key={i} value={app} disabled={isDisabled(app, mscApp1)}>{app}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="d1">Disziplin 1:</label><br />
              <select id="d1" value={mscDis1} onChange={(e) => setMscDis1(e.target.value)}>
                <option value="">Alle</option>
                {masterDiszList.map((d, i) => (
                  <option key={i} value={d} disabled={isDisabled(d, mscDis2)}>{d}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="d2">Disziplin 2:</label><br />
              <select id="d2" value={mscDis2} onChange={(e) => setMscDis2(e.target.value)}>
                <option value="">Alle</option>
                {masterDiszList.map((d, i) => (
                  <option key={i} value={d} disabled={isDisabled(d, mscDis1)}>{d}</option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>

      <div className="calendar-legend">
        <div><span className="legend-dot inf" /> Informatik/Mathematik</div>
        <div><span className="legend-dot digi" /> Digitalisierungsprojekte</div>
        <div><span className="legend-dot anw" /> Anwendungsgebiete</div>
        <div>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#06A69E',
              display: 'inline-block',
              marginRight: 6
            }}
          />
          Bearbeitet
        </div>
      </div>

      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay'
        }}
        events={calendarEvents}
        height="auto"
        eventClick={({ event }) => {
          const p = event.extendedProps || {};
          const id = event.id ?? p.id ?? null;

          setModalEvent({
            id,
            title: event.title,
            start: event.start,
            end: event.end,
            origin: p.origin,
            location: p.location,
            lecturer: p.lecturer,
            degree: p.course_degree,
            semester: p.course_semester,
            application: p.course_application,
            manuallyEdited: p.manuallyEdited
          });
        }}
      />

      {modalEvent && (
        <Modal onClose={() => setModalEvent(null)}>
          <h2>{modalEvent.title}</h2>
          <p><strong>Studiengang:</strong> {modalEvent.degree}</p>
          <p><strong>Semester:</strong> {modalEvent.semester}</p>
          <p><strong>Anwendung:</strong> {modalEvent.application}</p>
          <p><strong>Herkunft:</strong> {modalEvent.origin}</p>
          <p><strong>Ort:</strong> {modalEvent.location}</p>
          <p><strong>Dozent*in:</strong> {modalEvent.lecturer}</p>
          <p><strong>Start:</strong> {modalEvent.start?.toLocaleString()}</p>
          <p><strong>Ende:</strong> {modalEvent.end?.toLocaleString()}</p>
          <p>
            <strong>Status:</strong>{' '}
            {modalEvent.manuallyEdited ? 'Bearbeitet' : 'Noch nicht bearbeitet'}
          </p>

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <button
              className="primary"
              onClick={() => {
                if (modalEvent.id) {
                  setEditId(modalEvent.id);
                  setModalEvent(null);
                }
              }}
              disabled={!modalEvent.id}
            >
              Bearbeiten
            </button>
            <button className="secondary" onClick={() => setModalEvent(null)}>
              Schließen
            </button>
          </div>
        </Modal>
      )}

      {editId && (
        <CourseEditModal2
          data={{ id: editId }}
          season={semesterShort}
          onClose={() => setEditId(null)}
          onSave={() => {
            setEditId(null);
            loadEvents();
          }}
        />
      )}
    </div>
  );
}